import type { CommentsPostCard_PostData } from '~publish/pages/Community/fragments/CommentsPostCard_Post'

/**
 * True when the post should use the media-first card variant
 * (large image/video with carousel navigation for multi-asset posts).
 *
 * - TikTok / YouTube: always media-first
 * - Instagram: media-first for reel/short/story posts (type-based only)
 * - Facebook: only for reels/shorts
 */
export function isMediaFirstPost(post: CommentsPostCard_PostData): boolean {
  const service = post.channelService?.toLowerCase()
  const type = post.metadata?.type
  if (!service) return false

  if (service === 'tiktok' || service === 'youtube') return true

  if (service === 'instagram') {
    return type === 'reel' || type === 'short' || type === 'story'
  }

  if (service === 'facebook') {
    return type === 'reel' || type === 'short'
  }

  return false
}

/**
 * Whether we can collect and display comments for this post.
 * Stories, Google Business posts, and Instagram reminder-based posts
 * don't support comments in Community.
 */
export function postSupportsComments(post: CommentsPostCard_PostData): boolean {
  if (post.metadata?.type === 'story') return false
  if (post.channelService === 'googlebusiness') return false
  if (
    post.channelService === 'instagram' &&
    post.schedulingType === 'notification'
  )
    return false
  return true
}

/**
 * Returns the display text for a post, falling back to YouTube metadata title
 * when post.text is empty (YouTube stores the video title in metadata.title).
 */
export function getPostDisplayText(
  post: CommentsPostCard_PostData,
): string | null | undefined {
  const youtubeTitle =
    post.metadata?.__typename === 'YoutubePostMetadata'
      ? post.metadata.title
      : null
  return post.text || youtubeTitle
}

/**
 * Determines if this is a media-only post (has assets but no text content)
 */
export const isMediaOnlyPost = (text: string | null | undefined): boolean => {
  return !text || text.trim() === ''
}

/* ---------------------------------------------------------------------------
 * Grid template areas for PostMiniature
 *
 * The card uses CSS Grid with named areas. There are 5 base layouts —
 * each one is a different combination of visible sections (header, body,
 * assets). A status-badge row is prepended independently when needed.
 * ------------------------------------------------------------------------- */

type GridLayoutKey =
  | 'media-only'
  | 'text-only'
  | 'text-and-assets'
  | 'header-only'
  | 'header-assets'

const BASE_LAYOUTS: Record<GridLayoutKey, string> = {
  'media-only': "'header header' 'assets .'",
  'text-only': "'header header' 'body body'",
  'text-and-assets': "'header assets' 'body assets'",
  'header-only': "'header header'",
  'header-assets': "'header assets'",
}

function getLayoutKey({
  condensed,
  renderBody,
  renderAssets,
  hasOnlyMediaContent,
}: {
  condensed: boolean
  renderBody: boolean
  renderAssets: boolean
  hasOnlyMediaContent: boolean
}): GridLayoutKey {
  // Media-only posts in normal mode get a special stacked layout
  if (hasOnlyMediaContent && !condensed) return 'media-only'

  if (renderBody && renderAssets) return 'text-and-assets'
  if (renderBody) return 'text-only'
  if (renderAssets) return 'header-assets'
  return 'header-only'
}

/**
 * Computes the CSS `grid-template-areas` value for the PostMiniature card.
 *
 * Picks one of 5 base layouts based on which sections are visible, then
 * optionally prepends a status-badge row for draft / needs-approval posts.
 */
export function getGridTemplateAreas({
  condensed,
  renderBody,
  renderAssets,
  hasOnlyMediaContent,
  hasStatusBadge,
}: {
  condensed: boolean
  renderBody: boolean
  renderAssets: boolean
  hasOnlyMediaContent: boolean
  hasStatusBadge: boolean
}): string {
  const base =
    BASE_LAYOUTS[
      getLayoutKey({ condensed, renderBody, renderAssets, hasOnlyMediaContent })
    ]

  if (!hasStatusBadge) return base

  const statusRow = condensed ? "'status assets' " : "'status status' "
  return statusRow + base
}
